import type { TaskColumnConfig, TaskColumnId } from '@voll-intelligence/domain';

import type { TaskBoardResponse, TaskWithCard } from '@web/data/repositories';
import { t } from '@web/i18n';
import { EmptyHint } from '@web/ui/core';
import { taskColumnTone } from '@web/ui/core/utils/board_tone';

import { ColumnHead } from './column_head';
import { TaskCard } from './task_card';

export interface TasksBoardProps {
  data: TaskBoardResponse;
  taskColumns: readonly TaskColumnConfig[];
  onSelect: (task: TaskWithCard) => void;
}

const COLUMN_HINT_KEY: Record<TaskColumnId, Parameters<typeof t>[0]> = {
  ai_handling: 'workspace.taskColumn.ai_handling.note',
  awaiting_human: 'workspace.taskColumn.awaiting_human.note',
  human_handling: 'workspace.taskColumn.human_handling.note',
  resolved: 'workspace.taskColumn.resolved.note',
};

/**
 * Tasks kanban — one column per derived task column, mirroring
 * `ConversationsBoard`. Labels come from `taskColumns` (per-org), tone
 * from the stable column ID via `taskColumnTone`; the per-column note
 * becomes the `ColumnHead` hint tooltip.
 */
export function TasksBoard({
  data,
  taskColumns,
  onSelect,
}: Readonly<TasksBoardProps>): React.JSX.Element {
  return (
    <div className="grid grid-cols-tasks-board gap-2.5 px-4" data-testid="tasks-board">
      {taskColumns.map((col) => {
        const tasks = data.columns[col.id];
        return (
          <section key={col.id} className="flex flex-col gap-2.5 min-w-0">
            <ColumnHead
              tone={taskColumnTone(col.id)}
              label={col.label}
              count={tasks.length}
              hint={t(COLUMN_HINT_KEY[col.id])}
            />
            <div className="flex flex-col gap-2.5 min-h-board-col">
              {tasks.length === 0 ? (
                <EmptyHint
                  density="compact"
                  className="flex min-h-task-card items-center justify-center"
                >
                  {t('workspace.empty.column')}
                </EmptyHint>
              ) : (
                tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onClick={() => {
                      onSelect(task);
                    }}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
